import React, { useState, useMemo } from "react";
import BurArticlesTable from "./BurArticlesTable";
import ReportArticleModal from "../ReportArticleModal";
import EditBurArticleFlagsModal from "../EditBurArticleFlagsModal";
import OperatorAutocomplete from "./OperatorAutocomplete";
import {
  reprocessBurArticle,
  reportBurArticle,
  closeBurArticle,
  updateBurArticleFlags,
} from "../../../services/api";
import LabelManagementPanel from "../labels/LabelManagementPanel";
import LabelFilter from "../labels/LabelFilter";
import { useLabels } from "../labels/useLabels";

interface BurArticleItem {
  id: number;
  title: string;
  url: string;
  scrapedAt: string;
  publishedAt: string | null;
  applicationDateFrom: string | null;
  applicationDateTo: string | null;
  isBur: boolean;
  isContinuous: boolean;
  isAloc: boolean;
  targetOverride: string | null;
  operator: {
    id: number;
    name: string;
    voivodeship: string;
    projectName: string | null;
    baseUrl: string;
    target: string | null;
  };
}

interface SortConfig {
  column:
    | "scrapedAt"
    | "publishedAt"
    | "applicationDateFrom"
    | "applicationDateTo";
  order: "ASC" | "DESC";
}

interface VoivodeshipCount {
  voivodeship: string;
  count: number;
}

interface TargetCount {
  target: string;
  count: number;
}

interface OperatorCount {
  operatorId: number;
  operatorName: string;
  projectName: string | null;
  voivodeship: string;
  count: number;
}

interface BurArticlesListProps {
  articles: BurArticleItem[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    limit: number;
    total: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  onPageChange?: (newPage: number) => void;
  onLimitChange?: (newLimit: number) => void;
  voivodeships?: VoivodeshipCount[];
  selectedVoivodeship?: string;
  onVoivodeshipChange?: (voivodeship: string) => void;
  targetCounts?: TargetCount[];
  selectedTarget?: string;
  onTargetChange?: (target: string) => void;
  operatorCounts?: OperatorCount[];
  selectedOperatorId?: number;
  onOperatorChange?: (operatorId: number | undefined) => void;
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  currentSort?: SortConfig | null;
  userRole?: string;
  onArticlesRefresh?: () => Promise<unknown>;
  showDebugInfo?: boolean;
  viewMode?: "admin" | "user";
  onViewModeChange?: (viewMode: "admin" | "user" | undefined) => void;
}

const BurArticlesList: React.FC<BurArticlesListProps> = ({
  articles,
  pagination,
  onPageChange,
  onLimitChange,
  voivodeships,
  selectedVoivodeship,
  onVoivodeshipChange,
  targetCounts,
  selectedTarget,
  onTargetChange,
  operatorCounts,
  selectedOperatorId,
  onOperatorChange,
  onSortChange,
  currentSort,
  userRole,
  onArticlesRefresh,
  showDebugInfo,
  viewMode,
  onViewModeChange,
}) => {
  const isAdmin = userRole === "admin";
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [labelFilterMode, setLabelFilterMode] = useState<"all" | "labeled" | string>("all");

  const articleIds = useMemo(
    () => articles.map((a) => a.id.toString()),
    [articles]
  );

  const {
    labels,
    articleLabels,
    labelsLoading,
    error: labelsError,
    createLabel,
    updateLabel: handleUpdateLabel,
    deleteLabel: handleDeleteLabel,
    setArticleLabel,
  } = useLabels({ articleType: "bur", articleIds });

  const [labelsErrorDismissed, setLabelsErrorDismissed] = useState(false);

  const filteredArticles = useMemo(() => {
    if (labelFilterMode === "all") return articles;
    if (labelFilterMode === "labeled") {
      return articles.filter((a) => articleLabels[a.id.toString()]);
    }
    return articles.filter(
      (a) => articleLabels[a.id.toString()]?.labelId === labelFilterMode
    );
  }, [articles, articleLabels, labelFilterMode]);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingArticle, setReportingArticle] = useState<{
    id: string | number;
    title: string;
    type: "kfs" | "bur";
  } | null>(null);

  const [editFlagsModal, setEditFlagsModal] = useState<{
    isOpen: boolean;
    article: BurArticleItem | null;
  }>({
    isOpen: false,
    article: null,
  });

  const handleReprocess = async (articleId: string | number) => {
    try {
      await reprocessBurArticle(articleId as number, {
        skipNotifications: true,
        forceReprocess: true,
      });
      setToastType("success");
      setToastMessage(
        `Artykuł ${articleId} został wysłany do ponownego przetwarzania`
      );
      setTimeout(() => setToastMessage(null), 3000);
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || error.message || "Wystąpił błąd";
      setToastType("error");
      setToastMessage(`Błąd: ${errorMsg}`);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  const handleClose = async (
    articleId: string | number,
    _type?: "kfs" | "bur"
  ) => {
    try {
      await closeBurArticle(articleId as number);
      const article = articles.find((a) => a.id === articleId);
      setToastType("success");
      setToastMessage(
        article
          ? `Nabór "${article.title}" został zakończony.`
          : `Nabór ${articleId} został zakończony.`
      );
      setTimeout(() => setToastMessage(null), 3000);
      await onArticlesRefresh?.();
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || error.message || "Wystąpił błąd";
      setToastType("error");
      setToastMessage(`Błąd: ${errorMsg}`);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  const handleReport = (articleId: string | number, type: "kfs" | "bur") => {
    const article = articles.find((a) => a.id === articleId);
    if (article) {
      setReportingArticle({
        id: articleId,
        title: article.title,
        type,
      });
      setReportModalOpen(true);
    }
  };

  const handleReportSubmit = async (reason?: string) => {
    if (!reportingArticle) return;

    try {
      await reportBurArticle(reportingArticle.id as number, reason);
      setToastType("success");
      setToastMessage("Dziękujemy za zgłoszenie! Zajmiemy się tym problemem.");
      setTimeout(() => setToastMessage(null), 5000);
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || error.message || "Wystąpił błąd";
      setToastType("error");
      setToastMessage(`Błąd: ${errorMsg}`);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  const handleEditFlags = (
    articleId: string | number,
    _type: "kfs" | "bur"
  ) => {
    const article = articles.find((a) => a.id === articleId);
    if (article) {
      setEditFlagsModal({
        isOpen: true,
        article,
      });
    }
  };

  const handleEditFlagsSubmit = async (data: {
    applicationDateFrom: string | null;
    applicationDateTo: string | null;
    isBur: boolean;
    isContinuous: boolean;
    isAloc: boolean;
    targetOverride: string | null;
    operatorId?: number;
  }) => {
    if (!editFlagsModal.article) return;

    try {
      await updateBurArticleFlags(editFlagsModal.article.id, data);
      setEditFlagsModal({ isOpen: false, article: null });
      setToastType("success");
      setToastMessage("Flagi naboru zostały zaktualizowane.");
      setTimeout(() => setToastMessage(null), 3000);
      await onArticlesRefresh?.();
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || error.message || "Wystąpił błąd";
      setToastType("error");
      setToastMessage(`Błąd podczas aktualizacji: ${errorMsg}`);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  return (
    <div>
      {/* Toast notification */}
      {toastMessage && (
        <div
          className={`fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 max-w-sm ${
            toastType === "success"
              ? "bg-green-600 text-white"
              : "bg-red-600 text-white"
          }`}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              {toastType === "success" ? (
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              ) : (
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              )}
            </svg>
            <span className="text-sm font-medium">{toastMessage}</span>
          </div>
        </div>
      )}

      {labelsError && !labelsErrorDismissed && (
        <div className="mx-6 mt-4 px-4 py-2 bg-red-50 border border-red-200 rounded-md flex items-center justify-between">
          <span className="text-sm text-red-700">
            Nie udało się pobrać etykiet. Odśwież stronę.
          </span>
          <button
            onClick={() => setLabelsErrorDismissed(true)}
            className="text-red-500 hover:text-red-700 ml-4"
            aria-label="Zamknij"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      <LabelManagementPanel
        labels={labels}
        onCreateLabel={createLabel}
        onUpdateLabel={handleUpdateLabel}
        onDeleteLabel={handleDeleteLabel}
      />

      <div className="px-6 py-4 border-b border-neutral-200">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-2">
            <div className="flex items-center space-x-2">
              {voivodeships && (
                <select
                  value={selectedVoivodeship || ""}
                  onChange={(e) => onVoivodeshipChange?.(e.target.value)}
                  className="border border-neutral-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Wszystkie województwa</option>
                  {voivodeships.map((voivodeship) => (
                    <option
                      key={voivodeship.voivodeship}
                      value={voivodeship.voivodeship}
                    >
                      {voivodeship.voivodeship} ({voivodeship.count})
                    </option>
                  ))}
                </select>
              )}
              {operatorCounts && onOperatorChange && (
                <OperatorAutocomplete
                  operators={operatorCounts}
                  selectedOperatorId={selectedOperatorId}
                  onOperatorChange={onOperatorChange}
                />
              )}
              {targetCounts && (
                <select
                  value={selectedTarget || ""}
                  onChange={(e) => onTargetChange?.(e.target.value)}
                  className="border border-neutral-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Wszystkie kategorie</option>
                  {targetCounts.map((targetCount) => (
                    <option key={targetCount.target} value={targetCount.target}>
                      {targetCount.target === "FIRMY" &&
                        `Pracodawcy (${targetCount.count})`}
                      {targetCount.target === "DOROSLI" &&
                        `Osoby dorosłe (${targetCount.count})`}
                      {targetCount.target === "DOROSLI_TRANSFORMACJA" &&
                        `Zielona transformacja (${targetCount.count})`}
                    </option>
                  ))}
                </select>
              )}
              {/* Admin view mode toggle */}
              {isAdmin && onViewModeChange && (
                <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-neutral-300">
                  <span className="text-sm text-neutral-600">Widok:</span>
                  <select
                    value={viewMode || "admin"}
                    onChange={(e) =>
                      onViewModeChange(
                        e.target.value === "admin" ? undefined : "user"
                      )
                    }
                    className="border border-neutral-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-amber-50"
                  >
                    <option value="admin">Admin (wszystkie)</option>
                    <option value="user">Użytkownik (aktywne)</option>
                  </select>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <LabelFilter
                labels={labels}
                filterMode={labelFilterMode}
                onFilterChange={setLabelFilterMode}
                totalCount={articles.length}
                filteredCount={filteredArticles.length}
              />
              {labelsLoading && (
                <span className="text-xs text-neutral-400">...</span>
              )}
            </div>
          </div>
          <div className="text-right">
            {pagination && pagination.total > 0 && (
              <div className="space-y-1">
                <div className="text-sm text-neutral-600">
                  artykułów:{" "}
                  {(pagination.currentPage - 1) * pagination.limit + 1}-
                  {Math.min(
                    pagination.currentPage * pagination.limit,
                    pagination.total
                  )}{" "}
                  z {pagination.total}
                </div>
                <div className="flex items-center space-x-2 justify-end">
                  <span className="text-sm text-neutral-600">Pokaż:</span>
                  <select
                    value={pagination.limit}
                    onChange={(e) => onLimitChange?.(parseInt(e.target.value))}
                    className="text-sm border border-neutral-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={500}>500</option>
                  </select>
                  <span className="text-sm text-neutral-600">na stronę</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {articles.length === 0 ? (
        <div className="p-8 text-center">
          <div className="text-neutral-400 mb-2">
            <svg
              className="mx-auto h-12 w-12"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-neutral-900">
            Brak artykułów BUR
          </h3>
          <p className="text-sm text-neutral-500">
            Nie znaleziono artykułów BUR spełniających wybrane kryteria.
          </p>
        </div>
      ) : (
        <BurArticlesTable
          articles={filteredArticles}
          onSortChange={onSortChange}
          currentSort={currentSort}
          onVoivodeshipClick={onVoivodeshipChange}
          userRole={userRole}
          onReprocess={handleReprocess}
          onReport={handleReport}
          onClose={handleClose}
          onEditFlags={handleEditFlags}
          showDebugInfo={showDebugInfo}
          labels={labels}
          articleLabels={articleLabels}
          onSetArticleLabel={setArticleLabel}
        />
      )}

      <ReportArticleModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onSubmit={handleReportSubmit}
        articleTitle={reportingArticle?.title || ""}
      />

      <EditBurArticleFlagsModal
        isOpen={editFlagsModal.isOpen}
        article={
          editFlagsModal.article
            ? {
                id: editFlagsModal.article.id,
                title: editFlagsModal.article.title,
                applicationDateFrom: editFlagsModal.article.applicationDateFrom,
                applicationDateTo: editFlagsModal.article.applicationDateTo,
                isBur: editFlagsModal.article.isBur,
                isContinuous: editFlagsModal.article.isContinuous,
                isAloc: editFlagsModal.article.isAloc,
                targetOverride: editFlagsModal.article.targetOverride,
                operatorTarget: editFlagsModal.article.operator.target,
                operatorId: editFlagsModal.article.operator.id,
                operatorName: editFlagsModal.article.operator.name,
              }
            : null
        }
        operators={operatorCounts}
        onClose={() => setEditFlagsModal({ isOpen: false, article: null })}
        onSubmit={handleEditFlagsSubmit}
      />

      {pagination && pagination.total > 0 && pagination.totalPages > 1 && (
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200">
          <div className="flex justify-center">
            <div className="flex items-center space-x-1">
              {/* First and Previous */}
              <button
                onClick={() => onPageChange?.(1)}
                disabled={!pagination.hasPrevious}
                className="px-2 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Pierwsza strona"
              >
                &lt;&lt;
              </button>
              <button
                onClick={() => onPageChange?.(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevious}
                className="px-2 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Poprzednia strona"
              >
                &lt;
              </button>

              {/* Page Numbers */}
              {(() => {
                const currentPage = pagination.currentPage;
                const totalPages = pagination.totalPages;
                const pages = [];

                // Always show first page
                if (currentPage > 3) {
                  pages.push(1);
                  if (currentPage > 4) {
                    pages.push("...");
                  }
                }

                // Show pages around current
                for (
                  let i = Math.max(1, currentPage - 2);
                  i <= Math.min(totalPages, currentPage + 2);
                  i++
                ) {
                  pages.push(i);
                }

                // Always show last page
                if (currentPage < totalPages - 2) {
                  if (currentPage < totalPages - 3) {
                    pages.push("...");
                  }
                  pages.push(totalPages);
                }

                return pages.map((page, index) =>
                  page === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-2 py-1 text-sm text-neutral-400"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => onPageChange?.(page as number)}
                      className={`px-3 py-1 text-sm border rounded ${
                        page === currentPage
                          ? "bg-primary-600 text-white border-primary-600"
                          : "border-neutral-300 hover:bg-neutral-100"
                      }`}
                    >
                      {page}
                    </button>
                  )
                );
              })()}

              {/* Next and Last */}
              <button
                onClick={() => onPageChange?.(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="px-2 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Następna strona"
              >
                &gt;
              </button>
              <button
                onClick={() => onPageChange?.(pagination.totalPages)}
                disabled={!pagination.hasNext}
                className="px-2 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Ostatnia strona"
              >
                &gt;&gt;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BurArticlesList;
